import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Upload,
  Calendar,
  Shield,
  Clock,
  CheckCircle,
  ArrowRight,
  MapPin,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { StatCard } from '@components/common/StatCard';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { getCurrentApplicant } from '@mocks/applicants';
import { getMyBookings } from '@mocks/bookings';
import { getDocumentProgress } from '@mocks/attachments';

export const ApplicantDashboard: React.FC = () => {
  const navigate = useNavigate();
  const profile = getCurrentApplicant();
  const bookings = getMyBookings();
  const docProgress = getDocumentProgress();

  // Get next confirmed booking
  const upcomingBooking = bookings.find(
    (b) => b.status === 'CONFIRMED' && new Date(b.slot?.date || '') > new Date()
  );

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Banner */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-ncaa-dark">
          Welcome back, {profile.first_name}!
        </h1>
        <p className="text-ncaa-medium mt-2">
          Here's what's happening with your aviation license application
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Application Status"
          value="Under Review"
          icon={<FileText className="h-6 w-6" />}
          variant="info"
          trend="2 steps completed"
          trendUp
          onClick={() => navigate('/status')}
        />
        <StatCard
          title="Documents"
          value={`${docProgress.uploaded}/${docProgress.total}`}
          icon={<Upload className="h-6 w-6" />}
          variant="warning"
          action="Upload remaining"
          onClick={() => navigate('/documents')}
        />
        <StatCard
          title="Next Appointment"
          value={upcomingBooking ? new Date(upcomingBooking.slot?.date || '').toLocaleDateString() : 'None'}
          icon={<Calendar className="h-6 w-6" />}
          variant="success"
          onClick={() => navigate('/appointments')}
        />
        <StatCard
          title="NIN Status"
          value={profile.nin_status}
          icon={<Shield className="h-6 w-6" />}
          variant={profile.nin_status === 'VERIFIED' ? 'success' : 'warning'}
          trend={profile.nin_status === 'VERIFIED' ? 'Verified' : 'Pending verification'}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upcoming Booking Card */}
          {upcomingBooking && upcomingBooking.slot && (
            <Card
              title="Upcoming Appointment"
              icon={<Calendar className="h-5 w-5" />}
              actions={
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate('/appointments')}
                >
                  View All
                </Button>
              }
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-ncaa-primary" />
                      <span className="text-lg font-semibold text-gray-900">
                        {formatDate(upcomingBooking.slot.date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>
                        {upcomingBooking.slot.start_time} - {upcomingBooking.slot.end_time}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{upcomingBooking.center?.name}</span>
                    </div>
                  </div>
                  <Badge variant="success" icon={<CheckCircle className="h-4 w-4" />}>
                    Confirmed
                  </Badge>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <strong>Booking Reference:</strong> {upcomingBooking.booking_reference}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Please arrive 15 minutes before your appointment time.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Quick Actions Card */}
          <Card title="Quick Actions" icon={<ArrowRight className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/apply')}
                className="flex items-center gap-3 p-4 rounded-lg border-2 border-gray-200 hover:border-ncaa-primary hover:bg-blue-50 transition-all duration-200"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Apply for License</p>
                  <p className="text-sm text-gray-500">Start new application</p>
                </div>
              </button>

              <button
                onClick={() => navigate('/booking')}
                className="flex items-center gap-3 p-4 rounded-lg border-2 border-gray-200 hover:border-ncaa-primary hover:bg-blue-50 transition-all duration-200"
              >
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-green-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Book Appointment</p>
                  <p className="text-sm text-gray-500">Schedule test date</p>
                </div>
              </button>

              <button
                onClick={() => navigate('/documents')}
                className="flex items-center gap-3 p-4 rounded-lg border-2 border-gray-200 hover:border-ncaa-primary hover:bg-blue-50 transition-all duration-200"
              >
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Upload className="h-5 w-5 text-amber-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Upload Documents</p>
                  <p className="text-sm text-gray-500">{docProgress.total - docProgress.uploaded} remaining</p>
                </div>
              </button>

              <button
                onClick={() => navigate('/status')}
                className="flex items-center gap-3 p-4 rounded-lg border-2 border-gray-200 hover:border-ncaa-primary hover:bg-blue-50 transition-all duration-200"
              >
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Track Status</p>
                  <p className="text-sm text-gray-500">View progress</p>
                </div>
              </button>
            </div>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Profile Completion Card */}
          <Card title="Profile Completion" icon={<Shield className="h-5 w-5" />}>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium text-gray-700">Overall Progress</span>
                  <span className="font-semibold text-ncaa-primary">85%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-ncaa-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: '85%' }}
                  />
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">✓ Personal Information</span>
                  <Badge variant="success" size="sm">Complete</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">✓ NIN Verification</span>
                  <Badge variant="success" size="sm">Verified</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">⚠ Documents Upload</span>
                  <Badge variant="warning" size="sm">{docProgress.percentage}%</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">⋯ Skill Test</span>
                  <Badge variant="default" size="sm">Pending</Badge>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => navigate('/profile')}
              >
                View Profile
              </Button>
            </div>
          </Card>

          {/* Recent Activity Card */}
          <Card title="Recent Activity" icon={<Clock className="h-5 w-5" />}>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Document uploaded
                  </p>
                  <p className="text-xs text-gray-500">Skill test certificate • 2 days ago</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Appointment confirmed
                  </p>
                  <p className="text-xs text-gray-500">
                    {upcomingBooking?.center?.name} • 3 days ago
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Profile updated
                  </p>
                  <p className="text-xs text-gray-500">Contact information • 5 days ago</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-2 h-2 bg-gray-400 rounded-full mt-2" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    NIN verified
                  </p>
                  <p className="text-xs text-gray-500">Verification successful • 1 week ago</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
